import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { ApiService, RemitService } from '@app-core/services';
import { Dialog } from '@app-core/services/dialog';
import { PendingContractResponse, RemittedContractResponse } from '@app-model/api';
import { UserResponse } from '@app-model/login';
import { GetPendingContractRequest, GetRemittedContractRequest, RemitContractsRequest } from '@app-model/request';
import { RemitSubmittedPopupComponent } from '@app-shared/components';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import * as RemitActions from './remit.actions';
import * as remitReducer from './remit.reducer';
import * as fromRoot from '../reducers';
import { UpdatePendingNumberAction } from '../user/user.actions';

@Injectable()
export class RemitEffects {
  private actions$ = inject(Actions);
  private apiService = inject(ApiService);
  private store = inject<Store<fromRoot.State>>(Store);
  private _dialog = inject(Dialog);


  getPendingContracts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RemitActions.GET_PENDING_CONTRACTS),
      withLatestFrom(
        this.store.select((state) => state.remitState),
        this.store.select((state) => state.userState.user)
      ),
      mergeMap(([, remitState, user]: [RemitActions.GetPendingContractsAction, remitReducer.State, UserResponse]) => {
        const allDealers = JSON.parse(JSON.stringify(user.allDealers));

        if (allDealers?.length === 1) {
          allDealers[0].cmsDealerNumber = user.cmsDealerNumber;
        }

        const request: GetPendingContractRequest = {
          allDealers,
          cmsdealernumber: user.cmsDealerNumber,
          pendingDays: user.pendingDays,
          pendingToDeclineContracts: remitState.pendingToDeclineContracts,
          pendingToRemitContracts: remitState.pendingToRemitContracts,
          programId: user.programId,
        };

        return this.apiService.getPendingContracts(request).pipe(
          // If successful, dispatch success action with result
          map((response: PendingContractResponse) => {
            this.store.dispatch(new UpdatePendingNumberAction(response.pendingContracts.length));
            const filters = RemitService.fetchContractGroups(response.pendingContracts);

            return { type: RemitActions.GET_PENDING_CONTRACTS_SUCCESS, payload: { contracts: response, filters } };
          }),
          // If request fails, dispatch failed action
          catchError((payload) => of({ type: RemitActions.REMIT_FAILURE, payload }))
        );
      })
    )
  );

  submitPendingContracts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RemitActions.SUBMIT_ALL_PENDING_REMIT),
      withLatestFrom(
        this.store.select((state) => state.remitState),
        this.store.select((state) => state.userState.user)
      ),
      switchMap(([, remitState, user]: [RemitActions.GetPendingContractsAction, remitReducer.State, UserResponse]) => {
        const request: RemitContractsRequest = {
          allDealers: user.allDealers,
          cmsdealernumber: user.cmsDealerNumber,
          pendingDays: user.pendingDays,
          pendingToDeclineContracts: remitState.pendingToDeclineContracts,
          pendingToRemitContracts: remitState.pendingToRemitContracts,
          programId: user.programId,
        };

        return this.apiService.submitPendingContracts(request).pipe(
          // If successful, dispatch success action with result
          map((contracts: PendingContractResponse) => {
            const filters = RemitService.fetchContractGroups(contracts.pendingContracts);
            contracts = RemitService.resetContracts(contracts);

            this._dialog.open(RemitSubmittedPopupComponent, {
              data: { contracts, submittedContracts: remitState.pendingToRemitContracts },
            });

            return {
              type: RemitActions.SUBMIT_ALL_PENDING_REMIT_SUCCESS,
              payload: { submittedRemittedRequest: remitState.pendingToRemitContracts, contracts, filters },
            };
          }),
          // If request fails, dispatch failed action
          catchError((payload) => {
            console.error(payload);

            return of({ type: RemitActions.REMIT_FAILURE, payload });
          })
        );
      })
    )
  );

  getRemittedContracts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RemitActions.GET_REMITTED_CONTRACTS),
      withLatestFrom(
        this.store.select((state) => state.remitState),
        this.store.select((state) => state.userState.user)
      ),
      mergeMap(([, , user]: [RemitActions.GetRemittedContractsAction, remitReducer.State, UserResponse]) => {
        const request: GetRemittedContractRequest = {
          cmsdealernumber: user.cmsDealerNumber,
          programId: user.programId,
          allDealers: user.allDealers,
        };

        return this.apiService.getRemittedContracts(request).pipe(
          // If successful, dispatch success action with result
          map((contracts: RemittedContractResponse) => {
            const filters = RemitService.fetchRemittedContractGroups(contracts.remittedContracts);

            return { type: RemitActions.GET_REMITTED_CONTRACTS_SUCCESS, payload: { contracts, filters } };
          }),
          // If request fails, dispatch failed action
          catchError((payload) => of({ type: RemitActions.REMIT_FAILURE, payload }))
        );
      })
    )
  );
}
